<script setup>
import FixPanel from "@/components/approval-center/partials/ui/FixPanel.vue";
import FixPanelHeaderTitle from "@/components/approval-center/partials/ui/FixPanelHeaderTitle.vue";
import FixPanelHeader from "@/components/approval-center/partials/ui/FixPanelHeader.vue";
import MoveGroupTransition from "@/components/transitions/MoveGroupTransition.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import FixContent from "@/components/approval-center/partials/ui/FixContent.vue";
import FixText from "@/components/approval-center/partials/ui/FixText.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";
import SuccessFlow from "@/components/approval-center/modals/SuccessFlow.vue";
import SingleTextDisapprovalFlow from "@/components/approval-center/modals/SingleTextDisapprovalFlow.vue";
import {ref} from 'vue';
import {useApprovals} from "@/composables/approval-center/useApprovals";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import LoadMoreButton from "@/components/approval-center/partials/buttons/LoadMoreButton.vue";
import FixPanelSubText from "@/components/approval-center/partials/ui/FixPanelSubText.vue";
import FixPanelFooter from "@/components/approval-center/partials/ui/FixPanelFooter.vue";
import DefaultBanner from "@/components/approval-center/partials/DefaultBanner.vue";

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
});

const {
  showSuccessFlow,
  showDisapprovalFlow,
  handleConfirmMutation,
  handleConfirmForDisapprovalMutation,
  handleClose,
  selectedApproval,
  selectApprovalForApprove,
  selectApprovalForDisapprove,
} = useApprovalFlow();

const {mutate: handleConfirm} = handleConfirmMutation();
const {mutate: handleConfirmForDisapproval} = handleConfirmForDisapprovalMutation();
const answerText = ref('');

const {
  approvals,
  isPending,
} = useApprovals(props.category);

const isMultipleHeadingFix = (approval) => !!approval.approvable?.input?.headings
  && !!approval.approvable?.input?.headings[0];

const handleDisapprovalFlow = (approval) => {
  answerText.value = approval?.approvable?.output?.heading || approval.approvable?.output?.h1?.updated.heading;

  selectApprovalForDisapprove(approval);
};
</script>

<template>
  <FixPanel v-if="isPending || approvals.length">
    <FixPanelHeader class="tw-flex-col !tw-items-start">
      <FixPanelHeaderTitle>New Page Headings</FixPanelHeaderTitle>
      <FixPanelSubText>
        Your page headings have been updated, here are the revised versions I’ve drafted. Please review
        and approve.
      </FixPanelSubText>
    </FixPanelHeader>

    <LoadingBlock v-if="isPending" />

    <MoveGroupTransition class="tw-space-y-8">
      <FixPanel
        v-for="approval in approvals"
        :key="approval.id"
      >
        <div class="tw-flex lg:tw-flex-row tw-flex-col tw-gap-16 tw-justify-between tw-items-stretch">
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6">
            <FixPanelHeader>
              <FixPanelIcon icon-path="icons/settings-cog" />
              <FixPanelHeaderTitle>Before - {{ approval.approvable.input.page_path }}</FixPanelHeaderTitle>
            </FixPanelHeader>

            <FixContent variant="secondary">
              <FixText>
                <p v-if="approval.approvable.input.heading">
                  {{ approval.approvable.input.heading }}
                </p>
                <div v-else-if="isMultipleHeadingFix(approval)">
                  <p><strong>H1s:</strong></p>
                  <ul>
                    <li
                      v-for="(heading, index) in approval.approvable.input.headings"
                      :key="index"
                    >
                      {{ heading }}
                    </li>
                  </ul>
                </div>
              </FixText>
            </FixContent>
          </div>

          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6">
            <FixPanelHeader>
              <FixPanelIcon icon-path="icons/revision" />
              <FixPanelHeaderTitle>Revised</FixPanelHeaderTitle>
            </FixPanelHeader>

            <FixContent class="tw-flex tw-items-start tw-gap-8">
              <KitIcon />

              <FixText>
                <p v-if="approval.approvable?.output?.heading">
                  {{ approval.approvable.output.heading }}
                </p>
                <div
                  v-else-if="approval.approvable?.output?.h1"
                >
                  <p>
                    <strong>H1: </strong>
                    <span>{{ approval.approvable?.output?.h1.updated.heading }}</span>
                  </p>
                  <div v-if="approval.approvable?.output?.h2s">
                    <p><strong>Changed to H2s:</strong></p>
                    <ul>
                      <li
                        v-for="(heading, index) in approval.approvable.output.h2s"
                        :key="index"
                      >
                        {{ heading.inner_text }}
                      </li>
                    </ul>
                  </div>
                </div>
              </FixText>
            </FixContent>
          </div>
        </div>

        <FixPanelFooter
          :approval="approval"
          :category="category"
          @approve="selectApprovalForApprove(approval)"
          @revise="handleDisapprovalFlow(approval)"
        />
      </FixPanel>
    </MoveGroupTransition>
    <LoadMoreButton :category="category" />
  </FixPanel>

  <SuccessFlow
    v-if="showSuccessFlow"
    @confirm="handleConfirm({
      approval: selectedApproval,
      category: category,
    })"
    @close="handleClose"
  />

  <SingleTextDisapprovalFlow
    v-if="showDisapprovalFlow"
    v-model="answerText"
    @close="showDisapprovalFlow = false"
    @confirm="handleConfirmForDisapproval({
      approval: selectedApproval,
      newContent: answerText,
      category: category
    })"
  >
    <DefaultBanner v-if="isMultipleHeadingFix(selectedApproval)">
      <template #title>
        Note:
      </template>
      <template #default>
        We’ll update the first H1 heading for you.
        Any additional H1 headings will stay as they are but will be converted into H2 headings.
      </template>
    </DefaultBanner>
    <FixPanelHeader>
      <FixPanelIcon icon-path="icons/settings-cog" />
      <FixPanelHeaderTitle>Before - {{ selectedApproval?.approvable.input.page_path }}</FixPanelHeaderTitle>
    </FixPanelHeader>

    <FixContent variant="disabled">
      <FixText>
        <p v-if="isMultipleHeadingFix(selectedApproval)">
          {{ selectedApproval.approvable.input.headings[0] }}
        </p>
        <p v-else>
          {{ selectedApproval.approvable.input.heading }}
        </p>
      </FixText>
    </FixContent>

    <template #label>
      <FixPanelIcon icon-path="icons/revision" />
      Revised
    </template>
  </SingleTextDisapprovalFlow>
</template>
